import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { USE_MOCK, notificationsApi } from '@/api/client';
import type { SaveOmitirSettingsRequest, SaveEuropaSettingsRequest } from '@/api/client';
import type { NotificationSettingsDto, SendGeneralNotificationRequest } from '@/api/types';

const MOCK_SETTINGS: NotificationSettingsDto = {
  omitirEnabled: true,
  omitirThreshold: 3,
  omitirNotifyUser: true,
  omitirNotifyManager: true,
  omitirNotifyAdminOU: false,
  omitirUserTemplates: {},
  omitirMgrTemplates: {},
  europaEnabled: false,
  omitirOuOverrides: [],
  europaUserTemplates: {},
  europaMgrTemplates: {},
  generalOuOverrides: [],
};

export function useNotificationSettings() {
  return useQuery<NotificationSettingsDto>({
    queryKey: ['notificationSettings'],
    queryFn: async () => {
      if (USE_MOCK) return MOCK_SETTINGS;
      return notificationsApi.getSettings();
    },
  });
}

export function useSaveNotificationSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NotificationSettingsDto) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] saveNotificationSettings', body);
        return Promise.resolve();
      }
      return notificationsApi.saveSettings(body);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['notificationSettings'] }); },
  });
}

export function useSendGeneralNotification() {
  return useMutation({
    mutationFn: (body: SendGeneralNotificationRequest) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] sendGeneralNotification', body);
        return Promise.resolve({ recipientCount: body.recipientEmails.length, successCount: body.recipientEmails.length });
      }
      return notificationsApi.sendGeneral(body);
    },
  });
}

export function useSaveOmitirSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SaveOmitirSettingsRequest) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] saveOmitirSettings', body);
        return Promise.resolve();
      }
      return notificationsApi.saveOmitirSettings(body);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['notificationSettings'] }); },
  });
}

export function useSaveEuropaSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SaveEuropaSettingsRequest) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] saveEuropaSettings', body);
        return Promise.resolve();
      }
      return notificationsApi.saveEuropaSettings(body);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['notificationSettings'] }); },
  });
}

/** @deprecated Use useSaveNotificationSettings or useSendGeneralNotification */
export function useSaveNotificationTemplate() {
  return useSaveNotificationSettings();
}
